import { useIntercom } from '@/app/components/common/IntercomProvider';
import { TargetList } from '@/types/targetList';
import Pagination from '@/app/components/ui/Pagination';
import { ColumnDef, RowSelectionState } from '@tanstack/react-table';
import { useTranslations } from 'next-intl';
import { Checkbox } from '@/app/components/ui/Checkbox';
import Pill from '../ui/Pill';
import { targetListWorkflowStatusPillColors } from '@/constants/color';
import { formatDate, formatNumber, getErrorMessage } from '@/app/lib/utils';
import Table from '../ui/Table';
import { useMemo, useState } from 'react';
import { useRouter, useParams } from 'next/navigation';
import { ListResponse } from '@/types/common';
import { ListChecks, Loader } from 'lucide-react';
import { toast } from '@/app/hooks/use-toast';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { deleteTargetList, restoreTargetList } from '@/api/target-list';
import { useEntitlementGate } from '@/app/hooks/use-entitlement-gate';
import EntitlementGateModal from '@/app/components/entitlement/EntitlementGateModal';
import ConfirmationTrigger from '@/app/components/common/ConfirmationTrigger';
import TableRowActions from '@/app/components/common/TableRowActions';
import { useSalesforceEmbed } from '@/app/context/salesforce-embed';

interface TargetListTableProps {
  isLoading: boolean;
  targetListData?: ListResponse<TargetList>;
  page: number;
  limit: number;
  handlePageChange: (page: number, limit: number) => void;
  refetchTargetLists: () => void;
  hidePagination?: boolean;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (state: RowSelectionState) => void;
  showSelection?: boolean;
  contactColumnsOnly?: boolean;
  summaryColumnsOnly?: boolean;
  zeroStateOverlay?: React.ReactNode;
  skeletonRowCount?: number;
  emptyStateHeading?: string;
  emptyStateDescription?: string;
  isCardWrapper?: boolean;
  disableRowClick?: boolean;
}

const TargetListTable = ({
  isLoading,
  targetListData,
  page,
  limit,
  handlePageChange,
  hidePagination = false,
  rowSelection,
  onRowSelectionChange,
  showSelection = false,
  contactColumnsOnly = false,
  summaryColumnsOnly = false,
  refetchTargetLists,
  zeroStateOverlay,
  skeletonRowCount = 12,
  emptyStateHeading,
  emptyStateDescription,
  isCardWrapper = false,
  disableRowClick = false,
}: TargetListTableProps) => {
  const salesforceEmbed = useSalesforceEmbed();
  const isRowClickDisabled = disableRowClick || salesforceEmbed.isEmbed;
  const t = useTranslations('targetList');
  const { trackEvent } = useIntercom();
  const tCommon = useTranslations('common');
  const router = useRouter();
  const queryClient = useQueryClient();
  const { clientId } = useParams<{ clientId: string }>();
  const { guard, modalState, closeModal, openPlans } = useEntitlementGate(clientId);
  const [targetListToDelete, setTargetListToDelete] = useState<TargetList | null>(null);
  const [targetListToRestore, setTargetListToRestore] = useState<TargetList | null>(null);
  const {
    mutate: removeTargetList,
    isPending: isDeletingTargetList,
    variables: deletingTargetListId,
  } = useMutation({
    mutationFn: (targetListId: string) => deleteTargetList(clientId || '', targetListId),
    onSuccess: () => {
      toast({ title: t('deletedSuccessfully'), toastType: 'success' });
      setTargetListToDelete(null);
      refetchTargetLists();
      void queryClient.invalidateQueries({ queryKey: ['targetList-count'] });
    },
    onError: error => {
      toast({ title: getErrorMessage(error), toastType: 'error' });
    },
  });
  const {
    mutate: restoreTargetListMutation,
    isPending: isRestoringTargetList,
    variables: restoringTargetListId,
  } = useMutation({
    mutationFn: (targetListId: string) => restoreTargetList(clientId || '', targetListId),
    onSuccess: () => {
      toast({ title: t('restoredSuccessfully'), toastType: 'success' });
      setTargetListToRestore(null);
      refetchTargetLists();
      void queryClient.invalidateQueries({ queryKey: ['targetList-count'] });
    },
    onError: error => {
      toast({ title: getErrorMessage(error), toastType: 'error' });
    },
  });
  const columns: ColumnDef<TargetList>[] = useMemo(
    () => [
      ...(showSelection
        ? [
            {
              id: 'select',
              header: ({ table }: any) => (
                <div className="flex h-[37px] w-full items-center justify-center">
                  <Checkbox
                    checked={
                      table.getIsAllPageRowsSelected()
                        ? true
                        : table.getIsSomePageRowsSelected()
                          ? 'indeterminate'
                          : false
                    }
                    onCheckedChange={(value: boolean) => table.toggleAllPageRowsSelected(!!value)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                </div>
              ),
              cell: ({ row }: any) => (
                <div className="flex items-center justify-center h-full w-full">
                  <Checkbox
                    checked={row.getIsSelected()}
                    onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  />
                </div>
              ),
              size: 42,
              minSize: 42,
              maxSize: 42,
              enableSorting: false,
              enableHiding: false,
              enableResizing: false,
              meta: {
                isUtilityColumn: true,
                showHeaderDropdown: false,
                headerClassName:
                  'w-[42px] min-w-[42px] max-w-[42px] p-0 after:[background:var(--color-border)]',
                columnClassName: 'w-[42px] min-w-[42px] max-w-[42px] p-0',
                skeleton: { variant: 'checkbox' },
              },
            } as ColumnDef<TargetList>,
          ]
        : []),
      {
        id: 'name',
        accessorKey: 'name',
        header: t('table.name'),
        cell: ({ row }) => {
          const targetList = row.original;
          return (
            <div className="flex items-center gap-2">
              <ListChecks className="size-4 shrink-0 text-muted-foreground" />
              <span className="truncate font-medium text-foreground">{targetList.name}</span>
            </div>
          );
        },
        meta: {
          ...(contactColumnsOnly ? { showHeaderDropdown: false } : {}),
          skeleton: { variant: 'iconText', width: '55%' },
        },
        size: 38,
        enableResizing: !contactColumnsOnly,
      },
      {
        accessorKey: 'contact_count',
        header: t('table.contacts'),
        cell: ({ row }) => formatNumber(row.original.contact_count || 0),
        meta: {
          ...(contactColumnsOnly ? { showHeaderDropdown: false } : {}),
          skeleton: { variant: 'text', width: '35%' },
        },
        size: 14,
        enableResizing: !contactColumnsOnly,
      },
      {
        accessorKey: 'status',
        header: t('table.workflow'),
        cell: ({ row }) => {
          const status = row.original.status;
          const color = targetListWorkflowStatusPillColors[status];
          return (
            <div onClick={e => e.stopPropagation()}>
              <Pill color={color} hasDot>
                {status === 'building' ? (
                  <Loader className="size-3 animate-spin" />
                ) : (
                  <span className="size-2 rounded-full bg-current" />
                )}
                {t(`status.${status}`)}
              </Pill>
            </div>
          );
        },
        meta: {
          ...(contactColumnsOnly ? { showHeaderDropdown: false } : {}),
          skeleton: { variant: 'pill' },
        },
        size: 18,
        enableResizing: !contactColumnsOnly,
      },
      ...(contactColumnsOnly
        ? [
            {
              accessorKey: 'contact_added_at',
              header: t('table.contactAddedAt'),
              cell: ({ row }: { row: { original: TargetList } }) =>
                formatDate(row.original.contact_added_at || '', true),
              meta: {
                showHeaderDropdown: false,
                skeleton: { variant: 'text', width: '60%' },
              },
              size: 30,
              enableResizing: false,
            } as ColumnDef<TargetList>,
          ]
        : []),
      {
        id: 'created',
        accessorKey: 'created_at',
        header: t('table.createdAndUpdatedAt'),
        cell: ({ row }) => {
          const createdAt = row.original.created_at
            ? formatDate(row.original.created_at, true)
            : null;
          const updatedAt = row.original.updated_at
            ? formatDate(row.original.updated_at, true)
            : createdAt;

          if (!createdAt && !updatedAt) {
            return <span className="text-muted-foreground">-</span>;
          }

          return (
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-normal text-foreground truncate">
                {createdAt || '-'}
              </span>
              <span className="text-xs text-muted-foreground truncate">
                Updated: {updatedAt || '-'}
              </span>
            </div>
          );
        },
        meta: {
          width: 'calc(30% - 80px)',
          skeleton: { variant: 'text', width: '55%' },
        },
        size: 30,
      },
      {
        id: 'action',
        header: () => <div className="flex w-full justify-end">{t('table.action')}</div>,
        cell: ({ row }) => {
          const isDeleted = row.original.status === 'deleted';
          const isBusy = isDeleted
            ? isRestoringTargetList && restoringTargetListId === row.original.id
            : isDeletingTargetList && deletingTargetListId === row.original.id;
          return (
            <TableRowActions
              onOpenChange={open => {
                if (open)
                  trackEvent('target_list_overflow_open', {
                    current_path: window.location.pathname,
                  });
              }}
              ariaLabel={t('table.action')}
              busy={isBusy}
              disabled={isDeletingTargetList || isRestoringTargetList}
              actions={[
                isDeleted
                  ? {
                      value: 'restore',
                      label: t('actions.restore'),
                      type: 'restore',
                      onSelect: () => {
                        void guard({ resource: 'target_list', action: 'update' }, () => {
                          setTargetListToRestore(row.original);
                        });
                      },
                    }
                  : {
                      value: 'delete',
                      label: t('actions.delete'),
                      type: 'delete',
                      onSelect: () => {
                        trackEvent('target_list_overflow_delete', {
                          current_path: window.location.pathname,
                        });
                        void guard({ resource: 'target_list', action: 'delete' }, () => {
                          setTargetListToDelete(row.original);
                        });
                      },
                    },
              ]}
            />
          );
        },
        meta: { isActionColumn: true, clickable: false, skeleton: { variant: 'actions' } },
        size: 80,
        enableSorting: false,
        enableHiding: false,
        enableResizing: false,
      },
    ],
    [
      trackEvent,
      contactColumnsOnly,
      deletingTargetListId,
      guard,
      isDeletingTargetList,
      isRestoringTargetList,
      restoringTargetListId,
      showSelection,
      t,
    ]
  );

  const visibleColumns = contactColumnsOnly
    ? columns.filter(column => {
        const id = 'accessorKey' in column ? column.accessorKey : column.id;
        return (
          id === 'name' || id === 'contact_count' || id === 'status' || id === 'contact_added_at'
        );
      })
    : summaryColumnsOnly
      ? columns.filter(column => {
          const id = 'accessorKey' in column ? column.accessorKey : column.id;
          return id === 'name' || id === 'contact_count' || id === 'status';
        })
      : columns;

  const handleRowClick = (data: TargetList) => {
    trackEvent('target_list_open', { current_path: window.location.pathname });
    if (data.status === 'running') {
      router.push(`/${clientId}/target-list/${data.id}?tab=contacts`);
    } else {
      router.push(`/${clientId}/target-list/${data.id}?tab=workflow`);
    }
  };

  return (
    <div className="flex flex-col flex-1 items-stretch h-full justify-between overflow-hidden">
      <div className="relative flex-1 overflow-hidden">
        <Table
          onRowClick={isRowClickDisabled ? undefined : handleRowClick}
          rowClassName={
            isRowClickDisabled
              ? 'hover:bg-secondary/60 group'
              : 'cursor-pointer hover:bg-secondary/60 group'
          }
          tableId={
            contactColumnsOnly
              ? 'contact-target-list-table-v3'
              : summaryColumnsOnly
                ? 'summary-target-list-table-v1'
                : 'target-list-table'
          }
          columnSizingEnabled={
            !contactColumnsOnly && !summaryColumnsOnly && !isLoading && !zeroStateOverlay
          }
          showHeaderDropdown={
            !contactColumnsOnly && !summaryColumnsOnly && !isLoading && !zeroStateOverlay
          }
          data={targetListData?.data || []}
          isLoading={isLoading}
          skeletonRowCount={skeletonRowCount}
          emptySkeletonRowCount={zeroStateOverlay ? 12 : 0}
          columns={visibleColumns}
          showHeader={true}
          className="bg-background"
          noDataText={emptyStateHeading || t('noTargetListsData')}
          noDataDescription={emptyStateDescription ?? t('noTargetListsDataDescription')}
          enableRowSelection={showSelection}
          rowSelection={rowSelection}
          onRowSelectionChange={onRowSelectionChange}
          isCardWrapper={isCardWrapper}
          defaultPinning={{
            left: showSelection ? ['select', 'name'] : ['name'],
            right: ['action'],
          }}
        />
        {zeroStateOverlay}
      </div>
      {!hidePagination && targetListData?.total !== undefined && targetListData?.total > 0 && (
        <Pagination
          totalItems={targetListData?.total || 0}
          page={page}
          limit={limit}
          onChange={({ page, limit }) => {
            handlePageChange(page, limit);
          }}
        />
      )}
      <ConfirmationTrigger
        open={Boolean(targetListToDelete)}
        onOpenChange={open => {
          if (!open) setTargetListToDelete(null);
        }}
        title={t('deleteConfirmationTitle')}
        description={t('deleteConfirmationDescription')}
        confirmText={tCommon('delete')}
        cancelText={tCommon('cancel')}
        isLoading={isDeletingTargetList}
        onConfirm={() => {
          if (targetListToDelete) removeTargetList(targetListToDelete.id);
        }}
      />
      <ConfirmationTrigger
        open={Boolean(targetListToRestore)}
        onOpenChange={open => {
          if (!open) setTargetListToRestore(null);
        }}
        title={t('restoreConfirmationTitle')}
        description={t('restoreConfirmationDescription')}
        confirmText={tCommon('restore')}
        cancelText={tCommon('cancel')}
        isLoading={isRestoringTargetList}
        onConfirm={() => {
          if (targetListToRestore) restoreTargetListMutation(targetListToRestore.id);
        }}
      />
      <EntitlementGateModal
        open={modalState.isOpen}
        variant={modalState.variant}
        gateResult={modalState.gateResult}
        onOpenChange={open => {
          if (!open) closeModal();
        }}
        onViewPlans={openPlans}
      />
    </div>
  );
};

export default TargetListTable;
